import React, { useState, useEffect } from 'react';
import WordListCard from '../components/WordLists/WordListCard';
import CreateWordListForm from '../components/WordLists/CreateWordListForm';
import Modal from '../components/shared/Modal';
import LimitReachedModal from '../components/shared/LimitReachedModal';
import { api } from '../config/api';
import { useLimits } from '../hooks/useLimits';

const WordListsPage = () => {
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLimitReachedModalOpen, setIsLimitReachedModalOpen] = useState(false);
  const { listsLimit } = useLimits();

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await api.getWordLists();
      if (!Array.isArray(data)) {
        throw new Error('Nieprawidłowy format danych z serwera');
      }
      setLists(data);
    } catch (err) {
      console.error('Błąd podczas pobierania list:', err);
      setError(err.message || 'Wystąpił błąd podczas pobierania list');
      setLists([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateListSuccess = (newList) => {
    if (newList) {
      setLists(prevLists => [...prevLists, newList]);
    }
  };

  const handleUpdateList = (updatedList) => {
    setLists(prevLists => prevLists.map(list => 
      list.id === updatedList.id ? updatedList : list
    ));
  };

  const handleDeleteList = (listId) => {
    setLists(prevLists => prevLists.filter(list => list.id !== listId));
  };

  const handleAddClick = () => {
    if (lists.length >= listsLimit) {
      setIsLimitReachedModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold">Twoje listy słówek</h1>
        <button
          onClick={handleAddClick}
          className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
        >
          Dodaj
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-60">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      ) : !Array.isArray(lists) || lists.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-60 bg-white p-6 rounded-lg border border-neutral-200">
          <svg className="w-16 h-16 text-neutral-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
          </svg>
          <p className="text-neutral-500 text-sm mb-4">Nie masz jeszcze żadnych list słówek</p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
          >
            Utwórz pierwszą listę
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {lists.map(list => (
            <WordListCard
              key={list.id}
              list={list}
              onUpdate={handleUpdateList}
              onDelete={handleDeleteList}
            />
          ))}
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <CreateWordListForm
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleCreateListSuccess}
        />
      </Modal>

      <LimitReachedModal
        isOpen={isLimitReachedModalOpen}
        onClose={() => setIsLimitReachedModalOpen(false)}
      />
    </div>
  );
};

export default WordListsPage; 